import { useCallback } from 'react';

import { Platform } from 'react-native';

import { getBaseOs } from 'react-native-device-info';
import { useDispatch, useSelector } from 'react-redux';

import { NavigationProp } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';

import { SCREEN } from 'router/screens.ts';
import { RouteNavigationParams } from 'router/types.ts';

import { setSplashScreenState } from 'store/config/actions.ts';
import { getHaveToShowPinSetup } from 'store/user/selectors.ts';

import { RegularStorage, StorageKeys } from 'services/mmkv';

import useEnterTheApp from 'utils/hooks/appBootstrap/useEnterTheApp';
import {
    isMobileMediaQuery,
    isTabletMediaQuery,
    isWebMinWidthMediaQuery,
} from 'utils/responsiveness.ts';

function useOpenTheApp(
    navigation:
        | StackNavigationProp<RouteNavigationParams, SCREEN.ROOT>
        | NavigationProp<RouteNavigationParams>,
) {
    const dispatch = useDispatch();

    const haveToShowPinSetup = useSelector(getHaveToShowPinSetup);

    const { onEnterTheApp } = useEnterTheApp();

    const handleOpenTheApp = useCallback(async () => {
        const landingPageShown = await RegularStorage.getItem(
            StorageKeys.LANDING_PAGE_SHOWN,
        );
        const tutorialPageShown = await RegularStorage.getItem(
            StorageKeys.TUTORIAL_PAGE_SHOWN,
        );
        const baseOS = await getBaseOs();
        const isNativeOS = baseOS === 'Android' || baseOS === 'iOS';

        if (
            Platform.OS === 'web' &&
            (isNativeOS ||
                isMobileMediaQuery ||
                isTabletMediaQuery ||
                isWebMinWidthMediaQuery)
        ) {
            navigation?.reset({
                routes: [{ name: SCREEN.LANDING_SCREEN }],
            });
        } else if (!landingPageShown) {
            navigation?.reset({
                routes: [{ name: SCREEN.WELCOME_SCREEN }],
            });
        } else if (haveToShowPinSetup) {
            navigation?.reset({
                routes: [{ name: SCREEN.PIN_SETUP }],
            });
        } else if (!tutorialPageShown) {
            navigation?.reset({
                routes: [{ name: SCREEN.FTI_TUTORIAL }],
            });
        } else {
            await onEnterTheApp();
        }

        dispatch(
            setSplashScreenState({
                visible: false,
            }),
        );
    }, [haveToShowPinSetup, onEnterTheApp]);

    return { handleOpenTheApp };
}

export default useOpenTheApp;
